import { useEffect, type DependencyList } from "react";
import type { Socket } from "socket.io-client";
import { getSocket, onSocketChange } from "./ws-client";

// Primitiva ÚNICA de assinatura do socket singleton.
// Roda `setup(socket)` agora, sempre que o singleton muda (`onSocketChange`) e
// quando `deps` mudam; faz o cleanup do setup anterior a cada re-execução e no unmount.
// Resolve de forma uniforme a ordenação de efeitos: consumidores montados como
// descendentes do AppLayout (RealtimeBadge no Topbar; páginas via <Outlet/>) têm seu
// efeito ANTES do efeito que cria o socket no AppLayout, então no mount `getSocket()`
// ainda é null. Ao assinar `onSocketChange`, todos religam automaticamente quando o
// socket aparece — sem depender da ordem de declaração dos hooks. Todos os consumidores
// (status do badge, invalidação de cache, rooms) usam esta mesma primitiva.
export function useSocketEffect(
  setup: (socket: Socket | null) => (() => void) | void,
  deps: DependencyList
): void {
  useEffect(() => {
    let cleanup: (() => void) | void;
    const run = () => {
      cleanup?.();
      cleanup = setup(getSocket());
    };
    run();
    const unsubscribe = onSocketChange(run);
    return () => {
      unsubscribe();
      cleanup?.();
    };
    // `setup` é recriado a cada render (como o callback do useEffect); re-assinamos
    // quando `deps` mudam — mesmo contrato do useEffect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
